import type { PaintedRenderer, Point } from '../../../packages/view-phaser/src/painted-renderer.ts';

/** What the opening says: the full arrival by boat for a first life, a short one for every later life. */
export interface OpeningScript {
  full: boolean;
  predecessor?: { name: string; portrait: string; status: string };
  name: string;
  calling: string;
  portrait: string;
  /** Narration, as escaped HTML. */
  lines: readonly string[];
}
/** Where it happens: the walk ashore (boat to the saved arrival point) and the camera's first view over the river. */
export interface OpeningStage { path: readonly Point[]; wide: Point | null }

const esc = (value: string): string => value.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const ease = (t: number): number => t <= 0 ? 0 : t >= 1 ? 1 : .5 - Math.cos(Math.PI * t) / 2;
/** Painted pixels per second while stepping ashore: an unhurried walk. */
const WALK = 95;
export const OPENING_HINT = 'Click to walk or use · <kbd>V</kbd> surveys the district · Hold <kbd>Alt</kbd> to name things';
/** A touch screen has no V or Alt key: the same first-life hint, in taps. */
export const OPENING_HINT_TOUCH = 'Tap to walk or use · drag to look around';

/**
 * The opening, played in the engine over the saved arrival: display only, it never commits
 * story state, so skipping or reloading leaves the life exactly as saved. The HUD is hidden,
 * the scene fades up from black, the camera drifts from the river to the landing, the
 * character walks ashore under narration, then the HUD returns. Esc, Space, Enter or a click
 * skips straight to the end.
 */
export class Opening {
  #renderer: PaintedRenderer;
  #script: OpeningScript;
  #stage: OpeningStage;
  #reduced: boolean;
  #landed: () => void;
  #done: () => void;
  #overlay: HTMLElement;
  #line: HTMLElement;
  #cues: { at: number; run: () => void }[] = [];
  #fired = 0;
  #start = -1;
  #walkFrom: number;
  #walkLength = 0;
  #end: number;
  #finished = false;
  #turned = false;
  #key = (event: KeyboardEvent): void => {
    if (!['Escape', ' ', 'Enter'].includes(event.key)) return;
    event.preventDefault(); event.stopImmediatePropagation(); this.skip();
  };

  constructor(renderer: PaintedRenderer, script: OpeningScript, stage: OpeningStage, reduced: boolean, landed: () => void, done: () => void) {
    this.#renderer = renderer; this.#script = script; this.#stage = stage; this.#reduced = reduced; this.#landed = landed; this.#done = done;
    for (let i = 1; i < stage.path.length; i++) this.#walkLength += Math.hypot(stage.path[i]![0] - stage.path[i - 1]![0], stage.path[i]![1] - stage.path[i - 1]![1]);
    const overlay = this.#overlay = document.createElement('div');
    overlay.className = `opening ${script.full ? 'full' : 'short'}`;
    overlay.setAttribute('role', 'region'); overlay.setAttribute('aria-label', 'Arrival');
    overlay.innerHTML = `<div class="opening-veil"></div><div class="opening-bar"></div><div class="opening-bar bottom"></div><p class="opening-line" aria-live="polite"></p>`
      + (script.predecessor ? `<div class="opening-before"><img src="${esc(script.predecessor.portrait)}" alt=""><p><span>Before you</span><strong>${esc(script.predecessor.name)}</strong><span>${esc(script.predecessor.status)}</span></p></div>` : '')
      + `<div class="opening-third"><img src="${esc(script.portrait)}" alt=""><p><strong>${esc(script.name)}</strong><span>${esc(script.calling)}</span></p></div>`
      + '<button type="button" class="opening-skip" data-shell="arrive">Skip <kbd>Esc</kbd></button>';
    this.#line = overlay.querySelector('.opening-line')!;
    document.getElementById('play')!.dataset.opening = 'true';
    document.getElementById('play')!.append(overlay);
    overlay.addEventListener('pointerdown', () => this.skip());
    document.addEventListener('keydown', this.#key, true);
    overlay.querySelector<HTMLElement>('.opening-skip')!.focus({ preventScroll: true });
    // The timeline, in milliseconds from the first frame.
    const third = overlay.querySelector('.opening-third')!;
    let at = script.full ? 900 : 600;
    for (const text of script.lines) {
      const shown = Math.max(3000, text.length * 50);
      this.#cue(at, () => { this.#line.innerHTML = text; this.#line.classList.add('shown'); });
      this.#cue(at + shown, () => this.#line.classList.remove('shown'));
      at += shown + 400;
    }
    this.#walkFrom = script.full ? 3000 : 0;
    const walked = this.#walkFrom + this.#walkLength / WALK * 1000;
    this.#cue(script.full ? 4800 : 300, () => third.classList.add('shown'));
    this.#cue(script.full ? 9600 : 4300, () => third.classList.remove('shown'));
    this.#end = Math.max(at, walked + (script.full ? 900 : 0), script.full ? 10200 : 4600);
    this.#cues.sort((a, b) => a.at - b.at);
  }
  #cue(at: number, run: () => void): void { this.#cues.push({ at, run }); }
  /** Where the character stands this frame, and whether they are walking; the camera is directed here too. */
  frame(time: number, ready: boolean): { at: Point; moving: boolean } | null {
    if (this.#finished) return null;
    if (!ready) return { at: this.#stage.path[0]!, moving: false }; // the clock starts once the painting is drawn
    if (this.#start < 0) { this.#start = time; requestAnimationFrame(() => this.#overlay.classList.add('lifted')); }
    const t = time - this.#start;
    while (this.#fired < this.#cues.length && this.#cues[this.#fired]!.at <= t) this.#cues[this.#fired++]!.run();
    const walked = Math.max(0, t - this.#walkFrom) / 1000 * WALK, at = this.#along(walked), moving = t > this.#walkFrom && walked < this.#walkLength;
    if (!moving && walked >= this.#walkLength && !this.#turned) { this.#turned = true; this.#landed(); }
    // Drift from the wide view over the river to walking distance on the character; reduced motion cuts.
    const wide = this.#stage.wide, drift = !wide ? 1 : this.#reduced ? Number(t >= 3000) : ease((t - 1400) / 4000);
    this.#renderer.direct({ at: wide ? [wide[0] + (at[0] - wide[0]) * drift, wide[1] + (at[1] - wide[1]) * drift] : at, closeness: drift });
    if (t >= this.#end) this.skip();
    return { at, moving };
  }
  #along(distance: number): Point {
    const path = this.#stage.path;
    for (let i = 1; i < path.length; i++) {
      const [ax, ay] = path[i - 1]!, [bx, by] = path[i]!, length = Math.hypot(bx - ax, by - ay);
      if (distance <= length) return [ax + (bx - ax) * distance / length, ay + (by - ay) * distance / length];
      distance -= length;
    }
    return path.at(-1)!;
  }
  /** Jump to the end state: the character ashore, the HUD back, one hint on a first life. */
  skip(): void {
    if (this.#finished) return;
    this.#finished = true;
    document.removeEventListener('keydown', this.#key, true);
    if (!this.#turned) { this.#turned = true; this.#landed(); }
    this.#renderer.direct(null);
    this.#overlay.classList.add('ending');
    window.setTimeout(() => this.#overlay.remove(), 700);
    const play = document.getElementById('play')!;
    delete play.dataset.opening; play.dataset.hudIn = 'true';
    window.setTimeout(() => { delete play.dataset.hudIn; }, 1200);
    if (this.#script.full) {
      const hint = document.createElement('p');
      hint.className = 'opening-hint'; hint.setAttribute('role', 'status'); hint.innerHTML = matchMedia('(hover:none)').matches ? OPENING_HINT_TOUCH : OPENING_HINT;
      document.querySelector('.viewport-wrap')!.append(hint);
      const fade = (): void => { hint.classList.add('leaving'); window.setTimeout(() => hint.remove(), 600); };
      const timer = window.setTimeout(fade, 9000);
      document.getElementById('viewport')!.addEventListener('pointerdown', () => { window.clearTimeout(timer); window.setTimeout(fade, 1500); }, { once: true });
    }
    this.#done();
  }
}
